// WishlistAside — `/wishes` aside panel with 3 widgets per
// `docs/spec/wishlist/DESIGN.md §5.5` + `loop-goals/MASTER-PROMPT.md` outcome #7.
//
// 3 widgets (stacked vertically at desktop, slide-up BottomSheet at mobile):
//   1. **AsideWidgetRing** — current year completion rate (`completed / total`
//      with bounded edge case for `total = 0` showing "今年尚未建立心願")
//   2. **AsideWidgetStats** — 6 sub-stats: 總數 / 完成率 / 已完成 / 進行中 / 待完成 / 已放棄
//   3. **AsideWidgetUpcoming** — next 5 wishes with `target_date` within 30 days,
//      filtered to `status NOT IN ('completed', 'abandoned')`
//
// URL sync per `DESIGN.md §5.6`:
//   - Open: `history.pushState` + URL becomes `/wishes?aside=stats`
//   - Close: `history.back()` (single entry, not push)
//   - `popstate` listener auto-closes on browser back
//   - Esc key closes
//   - First-visit hint: cookie `wishlist_aside_dismissed=true` skips auto-open
//
// Per `DESIGN.md §5.4` "paper-deep" token (not v2 `bg-surface` white) for
// the aside bg + "hairline" border. Width 320px at desktop (per
// `DESIGN.md §3.6` 视觉对位 — not 360px v2 blur).
//
// W34.2.2 follow-up: on mobile the aside renders inside a `BottomSheet
// variant="mobile"` at 80vh (per SPEC-DRIFT-CHECK §13.1.2 follow-up). The
// 3 widgets stay the same; only the container swaps.

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, TrendingUp, X } from "lucide-react";
import { BottomSheet } from "@/components/patterns/BottomSheet";
import { useIsMobile } from "@/lib/hooks/useIsMobile";
import { WISH_STATUS_LABEL } from "@/lib/i18n/wishlist";
import {
  getWishStats,
  getUpcomingWishes,
  type WishStatsResponse,
  type UpcomingWishSummary,
} from "@/lib/api/wishlist";

const ASIDE_OPEN_KEY = "aside";
const COOKIE_KEY = "wishlist_aside_dismissed";

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match && match[1] ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, maxAgeDays: number) {
  if (typeof document === "undefined") return;
  const d = new Date();
  d.setTime(d.getTime() + maxAgeDays * 86_400_000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/`;
}

export function WishlistAside() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const open = searchParams.get(ASIDE_OPEN_KEY) === "stats";
  const isMobile = useIsMobile();

  const [hintDismissed, setHintDismissed] = useState<boolean>(true);
  useEffect(() => {
    setHintDismissed(readCookie(COOKIE_KEY) === "true");
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    return () => undefined;
  }, []);

  const close = useCallback(() => {
    if (!open) return;
    if (hintDismissed) {
      writeCookie(COOKIE_KEY, "true", 90);
    }
    navigate(-1);
  }, [open, hintDismissed, navigate]);

  if (!open) return null;

  const body = (
    <>
      <header className="flex items-center justify-between gap-s-2 mb-s-2">
        <h2 className="font-display text-body font-semibold text-ink-1">
          心願統計
        </h2>
        <button
          type="button"
          onClick={close}
          aria-label="關閉"
          data-testid="wishlist-aside-close"
          className="press min-h-11 min-w-11 flex items-center justify-center rounded-md text-ink-2 hover:bg-paper-elevated"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </header>
      <div className="flex flex-col gap-s-3">
        <AsideWidgetRing />
        <AsideWidgetStats />
        <AsideWidgetUpcoming />
      </div>
      {/* Auto-suggest toggle on first visit (DESIGN §5.6 first-visit hint) */}
      {!hintDismissed ? (
        <button
          type="button"
          onClick={() => {
            setHintDismissed(true);
            writeCookie(COOKIE_KEY, "true", 90);
          }}
          data-testid="wishlist-aside-dismiss-hint"
          className="press self-start min-h-11 px-s-3 rounded-full text-meta text-ink-3 hover:bg-paper-elevated"
        >
          不再自動顯示
        </button>
      ) : null}
    </>
  );

  if (isMobile) {
    // W34.2.2 mobile path: BottomSheet variant="mobile" full-width × 80vh
    // (per SPEC-DRIFT-CHECK §13.1.2 follow-up + DESIGN §11.1).
    return (
      <BottomSheet
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        variant="mobile"
        title="心願統計"
        className="max-h-[80vh] overflow-y-auto"
      >
        <aside
          data-component="WishlistAside"
          data-viewport="mobile"
          data-testid="wishlist-aside"
          role="complementary"
          aria-label="心願統計"
          className="flex flex-col gap-s-3"
        >
          {body}
        </aside>
      </BottomSheet>
    );
  }

  return (
    <aside
      data-component="WishlistAside"
      data-viewport="desktop"
      data-testid="wishlist-aside"
      role="complementary"
      aria-label="心願統計"
      className="bg-paper-deep border border-hairline rounded-2xl p-s-4 w-[320px] max-w-[92vw] fixed right-s-4 top-[80px] z-20 shadow-modal flex flex-col gap-s-3"
    >
      {body}
    </aside>
  );
}

// --- Widget 1: Ring (current year completion rate) --------------------

function AsideWidgetRing() {
  const { data } = useQuery({
    queryKey: ["wishes", "stats"],
    queryFn: () => getWishStats(),
    staleTime: 60_000,
  });
  const stats: WishStatsResponse = data ?? defaultStats();
  const total = stats.current_year_total;
  const completed = stats.current_year_completed;
  const percent = total > 0 ? Math.round((completed / total) * 100) : null;
  return (
    <section
      data-component="AsideWidgetRing"
      data-testid="aside-widget-ring"
      className="bg-paper-elevated border border-hairline rounded-2xl p-s-3 flex items-center gap-s-3"
    >
      <div
        className="relative h-14 w-14 rounded-full border-2 border-hairline"
        aria-hidden
      >
        <div
          className="absolute inset-0 rounded-full border-2 border-ink-1"
          style={{
            clipPath:
              percent !== null
                ? `polygon(50% 50%, 50% 0, ${50 + 50 * Math.sin((percent / 100) * 2 * Math.PI)}% ${50 - 50 * Math.cos((percent / 100) * 2 * Math.PI)}%, 50% 50%)`
                : "none",
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center text-meta font-semibold text-ink-1 tabular-nums">
          {percent !== null ? `${percent}%` : "—"}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-meta font-semibold text-ink-2">今年完成率</h3>
        <p className="text-meta text-ink-3 truncate">
          {total > 0
            ? `${completed} / ${total} 已完成`
            : "今年尚未建立心願"}
        </p>
      </div>
    </section>
  );
}

function defaultStats(): WishStatsResponse {
  return {
    total: 0,
    completed: 0,
    in_progress: 0,
    pending: 0,
    abandoned: 0,
    completion_rate: 0,
    upcoming: [],
    current_year_total: 0,
    current_year_completed: 0,
    current_year_completion_rate: 0,
  };
}

// --- Widget 2: Stats (6 sub-stats) -----------------------------------

function AsideWidgetStats() {
  const { data } = useQuery({
    queryKey: ["wishes", "stats"],
    queryFn: () => getWishStats(),
    staleTime: 60_000,
  });
  const s: WishStatsResponse = data ?? defaultStats();
  const items = [
    { label: "總數", value: s.total },
    {
      label: "完成率",
      value: `${Math.round(s.completion_rate * 100)}%`,
    },
    { label: WISH_STATUS_LABEL.completed, value: s.completed },
    { label: WISH_STATUS_LABEL.in_progress, value: s.in_progress },
    { label: WISH_STATUS_LABEL.pending, value: s.pending },
    { label: WISH_STATUS_LABEL.abandoned, value: s.abandoned },
  ];
  return (
    <section
      data-component="AsideWidgetStats"
      data-testid="aside-widget-stats"
      className="bg-paper-elevated border border-hairline rounded-2xl p-s-3"
    >
      <h3 className="flex items-center gap-s-2 text-meta font-semibold text-ink-2 mb-s-2">
        <TrendingUp className="h-3.5 w-3.5" aria-hidden />
        整體統計
      </h3>
      <dl className="grid grid-cols-2 gap-s-2">
        {items.map((it) => (
          <div key={it.label} className="flex flex-col">
            <dt className="text-meta text-ink-3">{it.label}</dt>
            <dd className="text-body-sm font-semibold text-ink-1 tabular-nums">
              {it.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

// --- Widget 3: Upcoming (next 5 within 30 days) -----------------------

function AsideWidgetUpcoming() {
  const { data } = useQuery({
    queryKey: ["wishes", "upcoming", 5],
    queryFn: () => getUpcomingWishes({ days: 30, limit: 5 }),
    staleTime: 60_000,
  });
  const items: UpcomingWishSummary[] = data ?? [];
  return (
    <section
      data-component="AsideWidgetUpcoming"
      data-testid="aside-widget-upcoming"
      className="bg-paper-elevated border border-hairline rounded-2xl p-s-3"
    >
      <h3 className="flex items-center gap-s-2 text-meta font-semibold text-ink-2 mb-s-2">
        <Calendar className="h-3.5 w-3.5" aria-hidden />
        即將到期（30 天內）
      </h3>
      {items.length === 0 ? (
        <p className="text-meta text-ink-3">30 天內沒有即將到期的項目</p>
      ) : (
        <ul className="flex flex-col gap-s-1">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`aside-upcoming-item-${it.id}`}
              className="flex items-center justify-between gap-s-1 text-meta"
            >
              <span className="truncate text-ink-1 flex-1">{it.name}</span>
              <span className="text-ink-3 tabular-nums shrink-0">
                {it.days_until === 0
                  ? "今天"
                  : it.days_until !== null
                    ? `${it.days_until} 天`
                    : "—"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
